/**
 * ConversationActionMenu — Shared ··· dropdown for chat header and tray rows
 *
 * Fixed positioning + backdrop overlay + outside-click/Escape close.
 * Menu items: Mark as unread, Mute/Unmute, Archive/Unarchive, Leave
 */

import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ConversationActionMenuProps {
  conversationId: string;
  isMuted: boolean;
  isArchived?: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onMarkUnread: () => void;
  onToggleMute: () => void;
  onToggleArchive: () => void;
  onLeave: () => void;
  onDelete?: () => void;
}

export const ConversationActionMenu = memo(function ConversationActionMenu({
  isMuted,
  isArchived,
  position,
  onClose,
  onMarkUnread,
  onToggleMute,
  onToggleArchive,
  onLeave,
  onDelete,
}: ConversationActionMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const style: React.CSSProperties = {
    position: 'fixed',
    top: Math.min(position.y, window.innerHeight - 220),
    left: Math.min(position.x, window.innerWidth - 200),
    zIndex: 200,
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[199]" onClick={onClose} />

      {/* Menu */}
      <div
        ref={menuRef}
        style={style}
        className="z-[200] min-w-[180px] animate-in fade-in zoom-in-95 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
      >
        <div className="py-1">
          {/* Mark as unread */}
          <MenuAction
            icon={
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            }
            label={t('messaging.markAsUnread')}
            onClick={() => { onMarkUnread(); onClose(); }}
          />

          {/* Mute / Unmute */}
          <MenuAction
            icon={
              isMuted ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.84 13a6 6 0 00-8.69-8.69L18.84 13zM1 1l22 22M9.41 9.41A4 4 0 0112 8v5l-2.59-3.59z" />
                  <path d="M6.68 14.32A8 8 0 014 10" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
              )
            }
            label={isMuted ? t('messaging.unmute') : t('messaging.mute')}
            onClick={() => { onToggleMute(); onClose(); }}
          />

          {/* Archive / Unarchive */}
          <MenuAction
            icon={
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
            }
            label={isArchived ? t('messaging.unarchive') : t('messaging.archive')}
            onClick={() => { onToggleArchive(); onClose(); }}
          />

          {/* Leave */}
          <MenuAction
            icon={
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            }
            label={t('messaging.leave')}
            onClick={() => { onLeave(); onClose(); }}
            danger
          />

          {/* Delete */}
          {onDelete && (
            <MenuAction
              icon={
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              }
              label={t('messaging.deleteConversation')}
              onClick={() => { onDelete(); onClose(); }}
              danger
            />
          )}
        </div>
      </div>
    </>
  );
});

function MenuAction({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
        danger
          ? 'text-error-600 hover:bg-error-50'
          : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
